import React, { useEffect, useRef, useState } from 'react';
import { Table } from 'react-bootstrap';
import {
    CheckCircle2,
    RefreshCw,
    ArrowRight,
    Undo2,
    ChevronDown,
    ChevronUp,
    MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatSek } from '../constants';
import { getApiUrl } from '../../../../utils/ApiUtils';
import axiosInstance from '../../../../services/AxiosInstance';
import VerificationDetailsModal from '../../Accounting/Verification/VerificationDetailsModal';

// Auto-check badge tint — the shared ye-badge palette (YearEndPage chrome).
// What the auto-check compared the ledger balance against, per check key.
const AUTO_CHECK_SOURCE = {
    skattekonto: 'saldo enligt importerade kontoutdrag från Skatteverket',
    kundfordringar: 'obetalda kundfakturor i LedgerLoop',
    leverantorsskulder: 'obetalda leverantörsfakturor i LedgerLoop',
    personalskatt: 'obetald personalskatt från bokförda lönekörningar',
    sociala_avgifter: 'obetalda sociala avgifter från bokförda lönekörningar',
    semesterloneskuld: 'beräknad semesterlöneskuld från anställdas semesterdagar',
};

/**
 * Plain-Swedish rendering of an auto-check's evidence in the expanded row —
 * the "why does this say Kontrollera" answer. auto_result shapes come from
 * YearEndProcessService._autoCheckForAccount.
 */
const AutoCheckEvidence = ({ item }) => {
    let result = item.auto_result;
    if (typeof result === 'string') {
        try { result = JSON.parse(result); } catch (e) { result = null; }
    }
    if (!result || item.auto_status === 'none') return null;

    if (result.check === 'bank_reconciliation') {
        return (
            <div className="ye-well" style={{ marginBottom: '0.75rem' }}>
                {result.unmatchedCount === 0
                    ? 'Alla banktransaktioner under året är avstämda mot bokföringen.'
                    : `${result.unmatchedCount} banktransaktioner under året är inte avstämda mot bokföringen ännu.`}
            </div>
        );
    }
    if (result.reason === 'no_balance_data') {
        return (
            <div className="ye-well" style={{ marginBottom: '0.75rem' }}>
                Inget saldo kunde läsas ur importerade Skatteverket-kontoutdrag
                {result.latestStatementDate ? ` (senaste transaktion: ${result.latestStatementDate})` : ''}
                {' '}— stäm av kontot manuellt mot skattekontot.
            </div>
        );
    }
    if (result.expected == null || result.booked == null) return null;

    const source = AUTO_CHECK_SOURCE[result.check] || 'systemets uppgifter';
    const agrees = Math.abs(Number(result.diff) || 0) <= 1;
    return (
        <div className="ye-well" style={{ marginBottom: '0.75rem' }}>
            <div>
                Bokfört saldo: <strong>{formatSek(result.booked)}</strong>
                {' '}· Förväntat enligt {source}: <strong>{formatSek(result.expected)}</strong>
            </div>
            <div style={{ marginTop: 2 }}>
                {agrees ? (
                    <span style={{ color: 'var(--bs-success)', fontWeight: 600 }}>Beloppen stämmer överens.</span>
                ) : (
                    <span style={{ color: 'var(--bs-warning)', fontWeight: 600 }}>
                        Differens: {formatSek(result.diff)} — kontrollera vad skillnaden beror på innan du attesterar.
                    </span>
                )}
            </div>
        </div>
    );
};

// Badge-vokabulär: "Ser bra ut" = automatkontrollen körde och stämmer.
// "Avvikelse — kontrollera" = automatkontrollen körde och saldot stämmer
// INTE med modulens data (fäll ut raden för jämförelsen). "Ingen automatisk
// kontroll" = det finns inget systemdata att jämföra mot — hela avstämningen
// är din. ("Kontrollera" vs "Manuell kontroll" gick inte att skilja åt.)
const AUTO_STATUS_META = {
    pass: { label: 'Ser bra ut', badge: 'success' },
    warn: { label: 'Avvikelse — kontrollera', badge: 'warning' },
    fail: { label: 'Avviker', badge: 'danger' },
    none: { label: 'Ingen automatisk kontroll', badge: 'neutral' },
};

/**
 * Vad varje konto stäms av MOT — svaret på "vad ska jag egentligen göra
 * här?". Klassisk bokslutspraxis: varje balanskonto har ett känt underlag.
 * Första träffen vinner; ordningen går från specifika konton till spann.
 */
const RECONCILIATION_GUIDANCE = [
    [/^1630$/, 'Stäm av mot ditt skattekonto hos Skatteverket — saldot per balansdagen ska vara samma.'],
    [/^16[45]\d$/, 'Momsfordran — ska motsvara momsdeklarationen för årets sista period (pengar du väntar på från Skatteverket).'],
    [/^151\d$/, 'Stäm av mot listan över obetalda kundfakturor — summan av det kunderna är skyldiga dig per balansdagen.'],
    [/^17\d\d$/, 'Stäm av mot underlagen för dina periodiseringar — förutbetalda kostnader och upplupna intäkter (kvitton, avtal, fakturor).'],
    [/^1910$/, 'Räkna kassan — kontanterna ska stämma med saldot per balansdagen.'],
    [/^19[123]\d$/, 'Stäm av mot bankens kontoutdrag per balansdagen — saldot ska vara exakt samma, ören inräknade.'],
    [/^1[012]\d\d$/, 'Stäm av mot anläggningsregistret under Tillgångar — anskaffningsvärden minus årets avskrivningar.'],
    [/^14\d\d$/, 'Stäm av mot din inventeringslista — lagret räknat och värderat per balansdagen.'],
    [/^208[1-8]$/, 'Aktiekapital m.m. ändras bara vid beslut — ska stämma med det som är registrerat hos Bolagsverket.'],
    [/^209[18]$/, 'Ska stämma med förra årets årsredovisning plus förra stämmans beslut om resultatet.'],
    [/^2099$/, 'Årets resultat — bokförs automatiskt i steget Skatt & dispositioner, ingen egen kontroll behövs.'],
    [/^21\d\d$/, 'Periodiseringsfonder och andra obeskattade reserver — ska stämma med tidigare års avsättningar (se tidigare deklarationer).'],
    [/^23\d\d$/, 'Stäm av mot årsbesked eller låneavi från långivaren — kvarvarande skuld per balansdagen.'],
    [/^244\d$/, 'Stäm av mot listan över obetalda leverantörsfakturor — summan av det du är skyldig leverantörer per balansdagen.'],
    [/^2514$/, 'Särskild löneskatt — ska motsvara beräkningen på årets pensionskostnader.'],
    [/^251\d$/, 'Skatteskulder — stäms av mot skatteberäkningen i steget Skatt & dispositioner.'],
    [/^26[0-6]\d$/, 'Moms — ska motsvara momsdeklarationen för årets sista period, som betalas efter årsskiftet.'],
    [/^2710$/, 'Personalskatt för årets sista lönekörning — ska motsvara AGI:n som lämnas och betalas i januari.'],
    [/^2731$/, 'Sociala avgifter för årets sista lönekörning — ska motsvara AGI:n som lämnas och betalas i januari.'],
    [/^2898$/, 'Beslutad men ej utbetald utdelning — ska stämma med stämmoprotokollet.'],
    [/^29[24]0$/, 'Semesterlöneskuld — beräknas från anställdas sparade semesterdagar och justeras i steget Bokslutshändelser.'],
    [/^29\d\d$/, 'Stäm av mot underlagen för upplupna kostnader och förutbetalda intäkter (fakturor som kommer efter årsskiftet).'],
    [/^28\d\d$/, 'Övriga skulder — kontrollera mot underlag (avtal, avräkningar) att skulden är riktig per balansdagen.'],
    [/^1\d\d\d$/, 'Kontrollera mot underlag — kontoutdrag, avtal eller kvitton — att tillgången finns och är rätt värderad per balansdagen.'],
    [/^2\d\d\d$/, 'Kontrollera mot underlag att skulden är riktig per balansdagen.'],
];

const guidanceFor = (accountNumber) => {
    const n = String(accountNumber || '');
    const hit = RECONCILIATION_GUIDANCE.find(([re]) => re.test(n));
    return hit ? hit[1] : null;
};

/**
 * Step 2 — Stäm av kontona: the reconciliation checklist. Each balance
 * account with a saldo gets a row the user attests ("jag har kontrollerat
 * att detta stämmer"), optionally with a note. Auto-checks from the
 * backend get a status badge. "Markera som klart" gates server-side
 * (409 until every row is attested).
 * Markup is the modern-card composition; the table is the app's
 * modern-table-container/modern-table pair (BookkeepingSection).
 */
const ReconciliationStep = ({
    items,
    processId,
    companyId,
    fiscalYearId,
    onRefresh,
    refreshing,
    onAttest,
    onUnattest,
    completed,
    onComplete,
    onUndo,
    busy,
    gateError,
}) => {
    const [expandedId, setExpandedId] = useState(null);
    const [noteDrafts, setNoteDrafts] = useState({});
    const [rowBusy, setRowBusy] = useState(null);
    const autoRefreshed = useRef(false);

    // Huvudboken per konto — verifikaten bakom varje saldo. Hämtas lasjt
    // (första gången en rad fälls ut) och cachas: payloaden är hela årets
    // huvudbok, nyckelad om till kontonummer.
    const [ledger, setLedger] = useState(null); // account_number -> {verifications, opening_balance}
    const [ledgerLoading, setLedgerLoading] = useState(false);
    const ledgerRequested = useRef(false);

    const ensureLedger = async () => {
        if (ledgerRequested.current || !fiscalYearId) return;
        ledgerRequested.current = true;
        setLedgerLoading(true);
        try {
            const { data } = await axiosInstance.get(
                getApiUrl(`${companyId}/reporting/ledger?fiscalYearId=${fiscalYearId}`),
                { withCredentials: true },
            );
            const byNumber = {};
            Object.values(data || {}).forEach((acc) => {
                if (acc?.account_number) byNumber[String(acc.account_number)] = acc;
            });
            setLedger(byNumber);
        } catch (err) {
            ledgerRequested.current = false; // tillåt nytt försök
        } finally {
            setLedgerLoading(false);
        }
    };

    // Verifikat-modal (samma som verifikatlistan/skattesteget).
    const [modalVerification, setModalVerification] = useState(null);
    const [showVerificationModal, setShowVerificationModal] = useState(false);

    const openVerification = async (verificationId) => {
        try {
            const { data: v } = await axiosInstance.get(
                getApiUrl(`${companyId}/verification/${verificationId}`),
                { withCredentials: true },
            );
            const debitSum = (v.transactions || [])
                .filter((t) => t.transaction_type === 'debit')
                .reduce((s, t) => s + Number(t.amount || 0), 0);
            setModalVerification({ ...v, debitSum, creditSum: debitSum });
            setShowVerificationModal(true);
        } catch (err) {
            toast.error('Kunde inte hämta verifikatet.');
        }
    };

    /** Kontots egna transaktioner i ett huvudboksverifikat, netto (debet −
     *  kredit) — det belopp verifikatet bidrar med till saldot. */
    const accountNet = (verification) => (verification.transactions || [])
        .reduce((s, t) => s + (t.transaction_type === 'debit' ? 1 : -1) * Number(t.amount || 0), 0);

    // First visit: the checklist is generated on demand — fetch it
    // automatically once if it's empty (guarded so a genuinely empty
    // result doesn't loop).
    useEffect(() => {
        if (!autoRefreshed.current && processId && !completed && (items || []).length === 0) {
            autoRefreshed.current = true;
            onRefresh();
        }
    }, [processId, completed, items, onRefresh]);

    const list = items || [];
    const attestedCount = list.filter((i) => Number(i.attested) === 1).length;

    const getNoteDraft = (item) =>
        noteDrafts[item.item_id] !== undefined ? noteDrafts[item.item_id] : (item.note || '');

    const handleToggleAttest = async (item) => {
        setRowBusy(item.item_id);
        try {
            if (Number(item.attested) === 1) {
                await onUnattest(item.item_id);
            } else {
                await onAttest(item.item_id, getNoteDraft(item));
            }
        } finally {
            setRowBusy(null);
        }
    };

    return (
        <>
            <style>{`
                /* Platt lista i kortet — ingen låda-i-låda (user find). Samma
                   dokumentspråk som ÅR-/blankettstegen: hårfina radlinjer,
                   tydlig rubrikrad, ingen egen bakgrund eller ram. */
                .ye-checklist-wrap { overflow-x: auto; }
                .ye-checklist-table { width: 100%; }
                .ye-checklist-table thead th {
                    font-size: 0.78rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    color: #6b7280;
                    background: transparent;
                    border-bottom: 1px solid #d1d5db;
                    padding: 0.5rem 0.6rem;
                }
                .ye-checklist-table tbody td {
                    padding: 0.55rem 0.6rem;
                    border-bottom: 1px solid #f3f4f6;
                    vertical-align: middle;
                    font-size: 0.875rem;
                    color: var(--title, #374151);
                    background: transparent;
                }
                .ye-checklist-table tbody tr:hover td { background: #fafcfb; }
                .ye-checklist-table td.ye-num {
                    text-align: right;
                    white-space: nowrap;
                    font-variant-numeric: tabular-nums;
                }
                .ye-checklist-table .ye-account-title { color: #6b7280; }
                .ye-checklist-table .ye-account-guidance {
                    font-size: 0.78rem;
                    color: #9ca3af;
                    margin-top: 0.15rem;
                    max-width: 52ch;
                    line-height: 1.45;
                    white-space: normal;
                }
                .ye-note-row td {
                    background: #f9fafb;
                    padding: 0.75rem 1rem;
                }
                .ye-note-label {
                    font-size: 0.78rem;
                    font-weight: 600;
                    color: #4b5563;
                    display: block;
                    margin-bottom: 0.35rem;
                }
                .ye-note-textarea {
                    width: 100%;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 0.5rem 0.75rem;
                    font-size: 0.85rem;
                    min-height: 60px;
                    transition: border-color 0.2s ease, box-shadow 0.2s ease;
                }
                .ye-note-textarea:focus {
                    outline: none;
                    border-color: var(--primary);
                    box-shadow: 0 0 0 3px var(--rgba-primary-1);
                }
                .ye-note-help { font-size: 0.75rem; color: #9ca3af; margin-top: 0.25rem; }
                .ye-icon-btn {
                    background: none;
                    border: none;
                    padding: 2px;
                    color: #6b7280;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                }
                .ye-icon-btn:hover { color: var(--primary); }
                .ye-row-verifikat { margin: 0.75rem 0; }
                .ye-verifikat-list {
                    list-style: none;
                    margin: 0.25rem 0 0;
                    padding: 0;
                    max-width: 640px;
                }
                .ye-verifikat-list li {
                    display: flex;
                    align-items: baseline;
                    gap: 0.75rem;
                    padding: 0.28rem 0;
                    border-bottom: 1px solid #f3f4f6;
                    font-size: 0.8125rem;
                    color: #374151;
                }
                .ye-verifikat-link {
                    background: none;
                    border: none;
                    padding: 0;
                    font: inherit;
                    font-weight: 600;
                    color: var(--primary);
                    cursor: pointer;
                    text-decoration: underline;
                    flex: 0 0 3.2rem;
                    text-align: left;
                }
                .ye-verifikat-date { flex: 0 0 5.6rem; color: #9ca3af; font-variant-numeric: tabular-nums; }
                .ye-verifikat-desc {
                    flex: 1;
                    min-width: 0;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                .ye-verifikat-amount {
                    white-space: nowrap;
                    font-variant-numeric: tabular-nums;
                    font-weight: 600;
                }
            `}</style>

            <VerificationDetailsModal
                showVerificationDetails={showVerificationModal}
                setShowVerificationDetails={setShowVerificationModal}
                selectedRow={modalVerification}
                onVerificationUpdated={() => {}}
                onUploadAttachment={() => {}}
                viewOnly
            />

            <div className="modern-card-header ye-step-header">
                <div>
                    <h4 className="section-title">Stäm av kontona</h4>
                    <p className="section-subtitle">
                        Listan är varje balanskonto som har ett saldo på balansdagen — varje
                        sådant konto blir en post i balansräkningen och behöver därför underlag.
                        Resultatkonton stäms inte av här; de kontrolleras via resultaträkningen.
                        Under varje konto står vad du jämför saldot med — till exempel bankens
                        kontoutdrag eller Skatteverkets skattekonto — och fäller du ut raden ser
                        du verifikaten bakom saldot. När det stämmer bockar du av kontot
                        (”attesterar”); stämmer det inte, ta reda på varför och rätta i
                        bokföringen först.
                    </p>
                </div>
                <div className="action-buttons">
                    <span className="ye-badge ye-badge-primary">
                        {attestedCount} av {list.length} attesterade
                    </span>
                    <button
                        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={onRefresh}
                        disabled={refreshing || completed}
                    >
                        <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
                        {refreshing ? 'Uppdaterar…' : 'Uppdatera listan'}
                    </button>
                </div>
            </div>

            <div className="modern-card-body">
                {refreshing && list.length === 0 ? (
                    <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>Hämtar kontolistan…</p>
                ) : list.length === 0 ? (
                    <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                        Inga konton att stämma av hittades. Klicka på ”Uppdatera listan” för att
                        bygga listan från bokföringen.
                    </p>
                ) : (
                    <div className="ye-checklist-wrap">
                        <Table responsive hover className="ye-checklist-table mb-0">
                            <thead>
                                <tr>
                                    <th>Konto</th>
                                    <th style={{ textAlign: 'right' }}>Saldo</th>
                                    <th>Automatisk kontroll</th>
                                    <th style={{ textAlign: 'center' }}>Attesterad</th>
                                    <th style={{ width: 40 }} aria-label="Anteckning" />
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((item) => {
                                    const attested = Number(item.attested) === 1;
                                    const autoMeta = AUTO_STATUS_META[item.auto_status] || AUTO_STATUS_META.none;
                                    const expanded = expandedId === item.item_id;
                                    const hasNote = !!(item.note || noteDrafts[item.item_id]);

                                    return (
                                        <React.Fragment key={item.item_id}>
                                            <tr>
                                                <td>
                                                    <strong>{item.account_number}</strong>
                                                    <span className="ye-account-title"> — {item.title}</span>
                                                    {guidanceFor(item.account_number) && (
                                                        <div className="ye-account-guidance">
                                                            {guidanceFor(item.account_number)}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="ye-num">{formatSek(item.balance)}</td>
                                                <td>
                                                    <span className={`ye-badge ye-badge-${autoMeta.badge}`}>
                                                        {autoMeta.label}
                                                    </span>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        style={{ cursor: 'pointer' }}
                                                        checked={attested}
                                                        disabled={rowBusy === item.item_id || completed}
                                                        onChange={() => handleToggleAttest(item)}
                                                        aria-label={`Attestera konto ${item.account_number}`}
                                                    />
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <button
                                                        className="ye-icon-btn"
                                                        onClick={() => {
                                                            if (!expanded) ensureLedger();
                                                            setExpandedId(expanded ? null : item.item_id);
                                                        }}
                                                        title={hasNote ? 'Visa anteckning' : 'Visa verifikat och anteckning'}
                                                        aria-label="Verifikat och anteckning"
                                                    >
                                                        {hasNote && <MessageSquare size={13} style={{ marginRight: 3 }} />}
                                                        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                    </button>
                                                </td>
                                            </tr>
                                            {expanded && (
                                                <tr className="ye-note-row">
                                                    <td colSpan={5}>
                                                        <AutoCheckEvidence item={item} />
                                                        {(() => {
                                                            // Verifikaten bakom saldot — kontots händelser under
                                                            // året ur huvudboken, senaste först.
                                                            const acc = ledger?.[String(item.account_number)];
                                                            const vers = (acc?.verifications || []).slice().reverse();
                                                            const MAX = 12;
                                                            return (
                                                                <div className="ye-row-verifikat">
                                                                    <span className="ye-note-label">
                                                                        Verifikat på kontot under året
                                                                        {acc?.opening_balance != null && Math.abs(Number(acc.opening_balance)) > 0.005 && (
                                                                            <> · ingående saldo {formatSek(acc.opening_balance)}</>
                                                                        )}
                                                                    </span>
                                                                    {ledgerLoading && !ledger && (
                                                                        <div className="ye-note-help">Hämtar huvudboken…</div>
                                                                    )}
                                                                    {ledger && vers.length === 0 && (
                                                                        <div className="ye-note-help">
                                                                            Inga verifikat på kontot i år — saldot är oförändrat
                                                                            sedan årets början.
                                                                        </div>
                                                                    )}
                                                                    {vers.length > 0 && (
                                                                        <ul className="ye-verifikat-list">
                                                                            {vers.slice(0, MAX).map((v) => (
                                                                                <li key={v.verification_id}>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="ye-verifikat-link"
                                                                                        onClick={() => openVerification(v.verification_id)}
                                                                                    >
                                                                                        {v.series || 'V'}{v.number}
                                                                                    </button>
                                                                                    <span className="ye-verifikat-date">
                                                                                        {String(v.date).slice(0, 10)}
                                                                                    </span>
                                                                                    <span className="ye-verifikat-desc">{v.description}</span>
                                                                                    <span className="ye-verifikat-amount">
                                                                                        {formatSek(accountNet(v))}
                                                                                    </span>
                                                                                </li>
                                                                            ))}
                                                                            {vers.length > MAX && (
                                                                                <li className="ye-note-help">
                                                                                    … och {vers.length - MAX} till — se huvudboken
                                                                                    under Rapporter för hela listan.
                                                                                </li>
                                                                            )}
                                                                        </ul>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                        <label
                                                            className="ye-note-label"
                                                            htmlFor={`ye-note-${item.item_id}`}
                                                        >
                                                            Anteckning (frivillig) — t.ex. vad du stämde av saldot mot
                                                        </label>
                                                        <textarea
                                                            id={`ye-note-${item.item_id}`}
                                                            className="ye-note-textarea"
                                                            value={getNoteDraft(item)}
                                                            readOnly={attested || completed}
                                                            placeholder="Ex: Stämmer mot bankens kontoutdrag per 31 december."
                                                            onChange={(e) =>
                                                                setNoteDrafts((prev) => ({ ...prev, [item.item_id]: e.target.value }))
                                                            }
                                                        />
                                                        {attested ? (
                                                            <div className="ye-note-help">
                                                                Kontot är attesterat. Ta bort bocken om du vill ändra anteckningen.
                                                            </div>
                                                        ) : (
                                                            <div className="ye-note-help">
                                                                Anteckningen sparas när du bockar av kontot.
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}

                {gateError && (
                    <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                        <strong>{gateError.message}</strong>
                        {list.length > 0 && attestedCount < list.length && (
                            <div style={{ marginTop: '0.35rem' }}>
                                {list.length - attestedCount} konton är inte attesterade ännu — bocka av
                                dem i listan ovan när du har kontrollerat saldot.
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="ye-step-footer">
                {completed ? (
                    <>
                        <span className="ye-footer-done">
                            <CheckCircle2 size={18} style={{ color: 'var(--bs-success)' }} />
                            Avstämningen är markerad som klar
                        </span>
                        <button
                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                            onClick={onUndo}
                            disabled={busy}
                        >
                            <Undo2 size={14} />
                            Ångra markeringen
                        </button>
                    </>
                ) : (
                    <>
                        <span className="ye-footer-hint">
                            {list.length > 0 && attestedCount === list.length
                                ? 'Alla konton är attesterade — du kan gå vidare.'
                                : 'Alla konton behöver attesteras innan steget kan markeras som klart.'}
                        </span>
                        <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={onComplete}
                            disabled={busy}
                        >
                            {busy ? 'Sparar…' : 'Markera som klart'}
                            <ArrowRight size={16} />
                        </button>
                    </>
                )}
            </div>
        </>
    );
};

export default ReconciliationStep;
